import { createRouter, createWebHistory } from "vue-router";
// Layout
import AppLayout from "@/layouts/app/Authenticated.vue";
import PublicLayout from "@/layouts/app/NotAuthenticated.vue";
import ErrorLayout from "@/layouts/app/Error.vue";
// Components
import HomeView from "@/views/app/HomeView.vue";
import Profil from "@/views/app/profil/ProfilView.vue";
import Contact from "@/views/app/ContactView.vue";
import Company from "@/views/app/company/CompanyView.vue";
// |_ Campaigns
import NewCampaignView from "@/views/app/campaigns/newCampaign/NewCampaignView.vue";
import CampaignView from "@/views/app/campaigns/singleCampaign/SingleCampaignView.vue";
import AddCandidatesView from "@/views/app/campaigns/singleCampaign/AddCandidates.vue";
import EditCandidatesView from "@/views/app/campaigns/singleCampaign/EditCampaignView.vue";
// |_ Spaces
import Spaces from "@/views/app/spaces/SpacesView.vue";
import Space from "@/views/app/spaces/SpaceView.vue";
// |_ Not autenticathed
import LoginPage from "@/views/app/LoginView.vue";
import RegisterPage from "@/views/app/register/RegisterView.vue";
import JoinCompanyPage from "@/views/app/register/JoinCompanyView.vue";
import ForgotPassword from "@/views/app/ForgotPasswordView.vue";
import ResetPassword from "@/views/app/ResetPasswordView.vue";
// |_ Errors
import NotFoundPage from "@/views/app/errors/404.vue";
import MaintenancePage from "@/views/app/errors/Maintenance.vue";
// |_ Others
import TransactionPage from "@/views/app/TransactionView.vue";
import IntegrationPage from "@/views/app/integration/IndexView.vue";
// Stores
import { storeToRefs } from "pinia";
import { useUserStore } from "@/stores/user.js";
import { useCompanyStore } from "@/stores/company.js";
import { useAppStore } from "@/stores/app.js";

const appRouter = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/404",
      name: "notFoundPage",
      component: NotFoundPage,
      meta: {
        layout: ErrorLayout,
        document_title: "Yuzu - 404",
      },
    },
    {
      path: "/maintenance",
      name: "maintenancePage",
      component: MaintenancePage,
      meta: {
        layout: ErrorLayout,
        document_title: "Yuzu - Maintenance",
      },
    },
    // Not authenticated
    {
      path: "/login",
      name: "loginPage",
      component: LoginPage,
      meta: {
        isPublic: true,
        document_title: "Yuzu - Connexion",
      },
      beforeEnter: (to, from, next) => {
        if (to.redirectedFrom) {
          to.meta.from = to.redirectedFrom;
        }

        next();
      },
    },
    {
      path: "/register",
      name: "registerPage",
      component: RegisterPage,
      meta: {
        isPublic: true,
        document_title: "Yuzu - Inscription",
      },
    },
    {
      path: "/join",
      name: "joinCompany",
      component: JoinCompanyPage,
      meta: {
        isPublic: true,
        isTransverse: true,
        document_title: "Yuzu - Rejoindre l'organisation",
      },
    },
    {
      path: "/forgot-password",
      name: "forgotPassword",
      component: ForgotPassword,
      meta: {
        isPublic: true,
        document_title: "Yuzu - Mot de passe oublié",
      },
    },
    {
      path: "/password-reset",
      name: "resetPassword",
      component: ResetPassword,
      meta: {
        isPublic: true,
        document_title: "Yuzu - Réinitialiser mon mot de passe",
      },
    },
    // Authenticated
    {
      path: "/",
      name: "root",
      meta: {
        document_title: "Yuzu - Dashboard",
      },
    },
    {
      path: "/companies/:companyId",
      name: "home",
      component: HomeView,
      meta: {
        document_title: "Yuzu - Dashboard",
        title: "Dashboard",
      },
    },
    {
      path: "/companies/:companyId/newCampaign",
      name: "newCampaign",
      component: NewCampaignView,
      meta: {
        title: "Nouvelle campagne",
        customHelpCenter: true,
        document_title: "Yuzu - Créer une campagne",
      },
    },
    {
      path: "/companies/:companyId/spaces",
      name: "spaces",
      component: Spaces,
      meta: {
        title: "Espaces",
        document_title: "Yuzu - Mes espaces",
      },
    },
    {
      path: "/companies/:companyId/spaces/:spaceId",
      name: "space",
      component: Space,
      meta: {
        document_title: "Yuzu - Espace",
        parent: {
          name: "spaces",
        },
        customTitle: {
          store: "space",
          data: "currentSpace.name",
          fallback: "Yuzu - principal",
        },
      },
    },
    {
      path: "/companies/:companyId/spaces/:spaceId/campaigns/:campaignId",
      name: "campaign",
      component: CampaignView,
      meta: {
        document_title: "Yuzu - Campagne",
        parent: {
          name: "space",
        },
        customTitle: {
          store: "campaign",
          data: "currentCampaign.name",
        },
      },
    },
    {
      path: "/companies/:companyId/spaces/:spaceId/campaigns/:campaignId/edit",
      name: "editCampaign",
      component: EditCandidatesView,
      meta: {
        document_title: "Yuzu - Campagne",
        parent: {
          name: "campaign",
        },
        customTitle: {
          store: "campaign",
          data: "currentCampaign.name",
        },
      },
    },    
    {
      path: "/companies/:companyId/spaces/:spaceId/campaigns/:campaignId/candidates",
      name: "addCandidates",
      component: AddCandidatesView,
      meta: {
        parent: {
          name: "campaign",
        },
        document_title: "Yuzu - Ajouter des talents",
        title: "Ajouter des talents",
      },
    },
    {
      path: "/companies/:companyId/profil",
      name: "profil",
      component: Profil,
      meta: {
        document_title: "Yuzu - Paramètres du profil",
        title: "Paramètres du profil",
      },
    },
    {
      path: "/companies/:companyId/settings",
      name: "company",
      component: Company,
      meta: {
        document_title: "Yuzu - Paramètres organisation",
        title: "Paramètres organisation",
        permissions: ["billingHistory", "members"],
      },
    },
    {
      path: "/companies/:companyId/contact",
      name: "contact",
      component: Contact,
      meta: {
        document_title: "Yuzu - Nous contacter",
        title: "Contact",
      },
    },
    {
      path: "/companies/:companyId/transactions/:transactionId",
      name: "transactions",
      component: TransactionPage,
      meta: {
        document_title: "Yuzu - Confirmation de transaction",
      },
    },
    {
      path: "/integration-page",
      name: "integrationPage",
      component: IntegrationPage,
      meta: {
        title: "Page d'intégration",
      },
    },
    // Onboarding
    {
      path: "/companies/:companyId/spaces/:spaceId/campaigns/8d9740ed-56ac-4f1f-856f-df9d81f16c52",
      name: "campaign-demonstration",
      component: CampaignView,
      meta: {
        document_title: "Yuzu - Campagne",
        parent: {
          name: "space",
        },
        customTitle: {
          store: "onboarding",
          data: "currentCampaign.name",
        },
      },
    },
    // Others
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      redirect: { name: "notFoundPage" },
      component: ErrorLayout,
      meta: {
        layout: ErrorLayout,
      },
    },
  ],
});

appRouter.beforeEach(async (to, from, next) => {
  const appStore = useAppStore();

  appStore.currentRoute = to;

  const userStore = useUserStore();
  const { user, auth_token } = storeToRefs(userStore);

  const companyStore = useCompanyStore();

  document.title = to.meta.document_title;

  if (to.meta.isPublic) {
    if (!to.meta.layout) to.meta.layout = PublicLayout;

    if (to.meta.isTransverse) {
      return next();
    }

    if (auth_token.value.email_verified) {
      await userStore.getUser("loginPage");

      if (from.name === "home") {
        appStore.currentRoute = from;
        next(false);
      } else {
        next({
          name: "home",
          params: {
            companyId: companyStore.companies[0].company_id,
          },
        });
      }
    } else {
      next();
    }
  } else {
    if (!to.meta.layout) to.meta.layout = AppLayout;

    if (!user.value.user_id) {
      await userStore.getUser("loginPage", to.params.companyId);
    }

    const tokenIsValid =
      auth_token.value.email_verified &&
      auth_token.value.exp > Date.now() / 1000;

    if (tokenIsValid) {
      const rulesPermission = [];

      to.meta.permissions?.forEach((rule) => {
        rulesPermission.push(userStore.hasPermission(rule, 1));
      });

      if (
        to.name === "root" ||
        (rulesPermission.length > 0 && !rulesPermission.includes(true))
      ) {
        next({
          name: "home",
          params: {
            companyId: companyStore.companies[0].company_id,
          },
        });

        return;
      }

      next();
    } else {
      userStore.logout();
      next({ name: "loginPage" });
    }
  }
});

export default appRouter;
